import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CustomDateInputs from "../components/CustomDateInputs";
import HasError from "../components/HasError";
import IsLoading from "../components/IsLoading";
import PaginationButtonToolbar from "../components/PaginatedButtonToolbar";
import { formatDateAndTimeOnline } from "../components/DateAndTimeFunctions";
import useJWTInterceptors from "../authentication/useJWTInterceptors";
import NoAccessPage from "./NoAccessPage";
import NotFound from "./NotFound";

const MULTIGAS_DEVICE = "SAPA";
const MULTIGAS_DEVICE_IDS = [136, 137];
const PAGE_SIZE = 50;

const appendQuery = (url, query) => {
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}${query}`;
};

const buildMultigasQuery = (startDate, endDate,deviceId, includePage, pageNumber) => {
  const parameters = [
    `datetime_range_after=${formatDateAndTimeOnline(startDate)}`,
    `datetime_range_before=${formatDateAndTimeOnline(endDate)}`,
    `device=${MULTIGAS_DEVICE}`,
    `device_id=${deviceId}`,
  ];

  if (includePage) {
    parameters.push(`page=${pageNumber}`);
  }

  return parameters.join("&");
};

const formatJalaliDateTime = (dateTime) => {
  if (!dateTime || typeof dateTime !== "string") return "-";

  const datePart = dateTime.slice(0, 10);
  const timePart = dateTime.slice(11, 19);
  const date = new Date(`${datePart}T00:00:00`);

  if (Number.isNaN(date.getTime())) return dateTime;

  return `${new Intl.DateTimeFormat("fa-IR").format(date)} (${timePart})`;
};

const MultigasHistory = () => {
  const { theRouteUrl } = useParams();
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const informationData = useSelector(
    (state) => state.information.informationData
  );
  const informationLoading = useSelector(
    (state) => state.information.informationLoading
  );
  const informationError = useSelector(
    (state) => state.information.informationError
  );

  const historyUrl = process.env.REACT_APP_MULTIGAS_HISTORY_URL;
  const excelUrl = process.env.REACT_APP_MULTIGAS_EXCEL_URL;

  const yesterday = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const [selectedStartDateAndTime, setSelectedStartDateAndTime] =
    useState(yesterday);
  const [selectedEndDateAndTime, setSelectedEndDateAndTime] = useState(
    new Date()
  );
  const [appliedStartDateAndTime, setAppliedStartDateAndTime] =
    useState(yesterday);
  const [appliedEndDateAndTime, setAppliedEndDateAndTime] = useState(
    new Date()
  );
  const [currentPageData, setCurrentPageData] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPageNumber, setTotalPageNumber] = useState(0);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState(false);

  const station = useMemo(
    () =>
      Array.isArray(informationData)
        ? informationData.find((item) => item.slug === theRouteUrl)
        : null,
    [informationData, theRouteUrl]
  );

  const isMultigasStation =
    station?.device === MULTIGAS_DEVICE &&
    MULTIGAS_DEVICE_IDS.includes(Number(station?.device_id));

  const applyDateFilter = useCallback((startDate, endDate) => {
    setAppliedStartDateAndTime(startDate);
    setAppliedEndDateAndTime(endDate);
    setExcelError(false);
  }, []);

  useEffect(() => {
    if (
      !isLoggedIn ||
      informationLoading ||
      informationError ||
      !isMultigasStation ||
      !historyUrl
    ) {
      return;
    }

    let isCurrentRequest = true;

    const fetchMultigasHistory = async () => {
      setLoading(true);
      setError(false);

      const query = buildMultigasQuery(
        appliedStartDateAndTime,
        appliedEndDateAndTime,
        station.device_id,
        true,
        pageNumber
      );

      try {
        const response = await useJWTInterceptors.get(
          appendQuery(historyUrl, query)
        );

        if (!isCurrentRequest) return;

        const results = Array.isArray(response.data?.results)
          ? response.data.results
          : [];
        const totalCount = Number(response.data?.count) || 0;

        setCurrentPageData(results);
        setTotalPageNumber(Math.ceil(totalCount / PAGE_SIZE));
      } catch (requestError) {
        if (isCurrentRequest) {
          setError(requestError);
          setCurrentPageData([]);
        }
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
        }
      }
    };

    fetchMultigasHistory();

    return () => {
      isCurrentRequest = false;
    };
  }, [
    appliedEndDateAndTime,
    appliedStartDateAndTime,
    count,
    historyUrl,
    informationError,
    informationLoading,
    isLoggedIn,
    isMultigasStation,
    pageNumber,
    station?.device_id,
  ]);

  const downloadExcelReport = async () => {
    if (!excelUrl) {
      setExcelError(true);
      return;
    }

    setExcelLoading(true);
    setExcelError(false);

    const query = buildMultigasQuery(
      appliedStartDateAndTime,
      appliedEndDateAndTime,
      station.device_id,
      false,
      pageNumber
    );

    try {
      const response = await useJWTInterceptors.get(
        appendQuery(excelUrl, query),
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const startDate = formatDateAndTimeOnline(
        appliedStartDateAndTime
      ).replace(/%20|:/g, "-");
      const endDate = formatDateAndTimeOnline(appliedEndDateAndTime).replace(
        /%20|:/g,
        "-"
      );

      link.href = objectUrl;
      link.download = `multigas-${startDate}_${endDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (requestError) {
      setExcelError(requestError);
    } finally {
      setExcelLoading(false);
    }
  };

  if (!isLoggedIn) return <NoAccessPage />;

  if (informationLoading) return <IsLoading />;

  if (informationError) return <HasError />;

  if (!isMultigasStation) return <NotFound />;

  if (!historyUrl || !excelUrl) return <HasError />;

  if (loading && currentPageData === null) return <IsLoading />;

  return (
    <main className="container-fluid p-2 text-medium" dir="rtl">
      {/* <h1 className="h4 text-center my-3">
        تاریخچه سنسور چندگازه - SAPA 136
      </h1> */}
      <div className="row g-2 align-items-start">
        <CustomDateInputs
          type="multigas"
          startDateAndTime={selectedStartDateAndTime}
          endDateAndTime={selectedEndDateAndTime}
          setSelectedStartDateAndTime={setSelectedStartDateAndTime}
          setSelectedEndDateAndTime={setSelectedEndDateAndTime}
          setCount={setCount}
          count={count}
          excelLoading={excelLoading}
          downloadExcelReport={downloadExcelReport}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          onApply={applyDateFilter}
        />

        <section className="col-12 col-lg-9 col-xl-10 my-3 my-lg-5">
          {excelError ? (
            <div className="alert alert-danger text-center" role="alert">
              دریافت گزارش اکسل با خطا مواجه شد.
            </div>
          ) : null}

          {loading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "40vh" }}
            >
              <span
                className="spinner spinner-border"
                aria-label="در حال دریافت اطلاعات"
              />
            </div>
          ) : error ? (
            <HasError />
          ) : currentPageData.length === 0 ? (
            <div className="alert alert-secondary text-center" role="status">
              در بازه انتخاب‌شده داده‌ای یافت نشد.
            </div>
          ) : (
            <>
              {totalPageNumber > 1 ? (
                <div className="mb-2 overflow-auto" dir="ltr">
                  <PaginationButtonToolbar
                    pageNumber={pageNumber}
                    totalPageNumber={totalPageNumber}
                    setPageNumber={setPageNumber}
                  />
                </div>
              ) : null}

              <div className="table-responsive bg-secondary p-1">
                <table className="table table-light table-striped table-bordered table-sm mb-0 text-center align-middle">
                  <thead>
                    <tr>
                      <th className="p-3" scope="col">
                        VOC (ppm)
                      </th>
                      <th className="p-3" scope="col">
                        NH3 (ppm)
                      </th>
                      <th className="p-3 text-nowrap" scope="col">
                        تاریخ و ساعت
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageData.map((record, index) => (
                      <tr key={`${record.created_datetime}-${index}`}>
                        <td>{record.voc ?? "-"}</td>
                        <td>{record.nh3 ?? "-"}</td>
                        <td className="ss01 text-nowrap" dir="ltr">
                          {formatJalaliDateTime(record.created_datetime)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </main>
  );
};

export default MultigasHistory;
